import { LicensePlan } from '../../types';
import { 
  baseOfficeFeatures, 
  baseSecurityFeatures, 
  baseCollaborationFeatures 
} from '../features/commonFeatures';
import { businessSpecificFeatures } from '../features/businessFeatures';
import { mergeFeatures, combineFeatureSets } from '../../utils/licenseUtils';

const basicBaseFeatures = {
  'Exchange Online': '100 GB primary mailbox + 50 GB archive',
  'SharePoint Online': true,
  'Microsoft Teams': false,
  'Web versions of Office': true,
  'Desktop Office apps': false,
  'OneDrive for Business': '1 TB storage',
  'Basic Security': true,
  'Mobile Device Management': true,
  'URL time-of-click protection': true,
  'Copilot Chat enhancements': 'Inbox/calendar awareness + Word, Excel, PowerPoint agents',
  'Copilot Chat Analytics': true,
};

const basicWithTeamsFeatures = {
  ...basicBaseFeatures,
  'Microsoft Teams': true,
};

const standardBaseFeatures = {
  ...basicBaseFeatures,
  'Desktop Office apps': true,
  'Bookings': true,
  'MileIQ': true,
};

const standardWithTeamsFeatures = {
  ...standardBaseFeatures,
  'Microsoft Teams': true,
};

const premiumBaseFeatures = {
  ...standardBaseFeatures,
  'Exchange Online': '100 GB primary mailbox + 1.5 TB archive',
  'Advanced Security': true,
  'Azure Information Protection P1': true,
  'Conditional Access': true,
  'Microsoft Defender for Business': true,
  'Microsoft Entra ID P1': true,
  'Microsoft Intune': true,
  'Power Apps': 'Limited use rights',
  'Power Automate': 'Limited use rights',
  // Premium has expanded mailbox/archive capacity; URL protection is Basic/Standard only per MS list
  'URL time-of-click protection': false,
};

const premiumWithTeamsFeatures = {
  ...premiumBaseFeatures,
  'Microsoft Teams': true,
};

const copilotBusinessFeatures = {
  'Microsoft 365 Copilot': true,
  'Copilot in Word': true,
  'Copilot in Excel': true,
  'Copilot in PowerPoint': true,
  'Copilot in Outlook': true,
  'Work IQ': true,
  'Researcher agent': true,
  'Analyst agent': true,
  'Facilitator agent': true,
  'Agent Builder and Copilot Studio': true,
  'Copilot connectors': true,
  'Copilot Cowork': 'Access included; usage is metered',
};

const withCopilotBusinessFeatures = (
  baseFeatures: Record<string, boolean | string>,
  withTeams: boolean
) => ({
  ...baseFeatures,
  ...copilotBusinessFeatures,
  'Copilot in Teams': withTeams,
});

const buildBusinessFeatures = (overrides: Record<string, boolean | string>) =>
  combineFeatureSets(
    mergeFeatures(baseOfficeFeatures, overrides),
    mergeFeatures(baseSecurityFeatures, overrides),
    mergeFeatures(baseCollaborationFeatures, overrides),
    mergeFeatures(businessSpecificFeatures, overrides)
  );

export const m365BusinessPlans: LicensePlan[] = [
  {
    id: 'm365-business-basic',
    name: 'Microsoft 365 Business Basic (no Teams)',
    description: 'Web and mobile apps, email, and cloud storage for small businesses (up to 300 users)',
    pricing: {
      points: [
        { amount: 4.4, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 5.4, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildBusinessFeatures(basicBaseFeatures)
  },
  {
    id: 'm365-business-basic-with-teams',
    name: 'Microsoft 365 Business Basic with Teams',
    description: 'Web and mobile apps, email, cloud storage, and Teams (up to 300 users)',
    pricing: {
      points: [
        { amount: 6.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 7.0, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildBusinessFeatures(basicWithTeamsFeatures)
  },
  {
    id: 'm365-business-standard',
    name: 'Microsoft 365 Business Standard (no Teams)',
    description: 'Desktop Office apps, web apps, and business services (up to 300 users)',
    pricing: {
      points: [
        { amount: 9.29, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 10.79, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildBusinessFeatures(standardBaseFeatures)
  },
  {
    id: 'm365-business-standard-with-teams',
    name: 'Microsoft 365 Business Standard with Teams',
    description: 'Desktop Office apps, web apps, business services, and Teams (up to 300 users)',
    pricing: {
      points: [
        { amount: 12.5, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 14.0, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildBusinessFeatures(standardWithTeamsFeatures)
  },
  {
    id: 'm365-business-standard-copilot',
    name: 'Microsoft 365 Business Standard with Copilot (no Teams)',
    description: 'Permanent Business Standard suite with Microsoft 365 Copilot Business (up to 300 users; Teams sold separately)',
    pricing: {
      points: [
        { amount: 20.3, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: buildBusinessFeatures(withCopilotBusinessFeatures(standardBaseFeatures, false))
  },
  {
    id: 'm365-business-standard-copilot-with-teams',
    name: 'Microsoft 365 Business Standard with Copilot',
    description: 'Permanent Business Standard suite with Microsoft 365 Copilot Business and Teams (up to 300 users)',
    pricing: {
      points: [
        { amount: 23.5, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: buildBusinessFeatures(withCopilotBusinessFeatures(standardWithTeamsFeatures, true))
  },
  {
    id: 'm365-business-premium',
    name: 'Microsoft 365 Business Premium (no Teams)',
    description: 'Advanced security, device management, and identity protection (up to 300 users)',
    pricing: {
      points: [
        { amount: 18.79, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: buildBusinessFeatures(premiumBaseFeatures)
  },
  {
    id: 'm365-business-premium-with-teams',
    name: 'Microsoft 365 Business Premium with Teams',
    description: 'Advanced security, device management, identity protection, and Teams (up to 300 users)',
    pricing: {
      points: [
        { amount: 22.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: buildBusinessFeatures(premiumWithTeamsFeatures)
  },
  {
    id: 'm365-business-premium-copilot',
    name: 'Microsoft 365 Business Premium with Copilot (no Teams)',
    description: 'Permanent Business Premium suite with Microsoft 365 Copilot Business (up to 300 users; Teams sold separately)',
    pricing: {
      points: [
        { amount: 28.8, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: buildBusinessFeatures(withCopilotBusinessFeatures(premiumBaseFeatures, false))
  },
  {
    id: 'm365-business-premium-copilot-with-teams',
    name: 'Microsoft 365 Business Premium with Copilot',
    description: 'Permanent Business Premium suite with Microsoft 365 Copilot Business and Teams (up to 300 users)',
    pricing: {
      points: [
        { amount: 32.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: buildBusinessFeatures(withCopilotBusinessFeatures(premiumWithTeamsFeatures, true))
  }
];
